import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api'

function CurriculoGerado({ user, onLogout, perfil, resume }) {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [descricaoVaga, setDescricaoVaga] = useState('');
  const [gerando, setGerando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [curriculo, setCurriculo] = useState('');

  // Carrega o currículo do uuid atual
  useEffect(() => {
    setMensagem('');
    setDescricaoVaga('');
    setCurriculo('');
    apiClient.get(`/api/generated_resume/${uuid}`)
      .then(res => setCurriculo(res.data.curriculo))
      .catch(err => setMensagem('Erro ao carregar currículo!'));
  }, [uuid, user.cliente_id]);

  function montarPerfilParaPrompt() {
    if (!perfil) return null;
    return {
      nome: perfil.nome || '',
      email: perfil.email || '',
      telefone: perfil.telefone || '',
      sites: resume?.sites || {},
      experienciaAcademica: resume?.academic_experience || [],
      experienciaProfissional: resume?.professional_experience || [],
      idiomas: resume?.languages || []
    };
  }

  const handleGerarCurriculo = async () => {
    setMensagem('');
    setGerando(true);
    try {
      const perfilTexto = montarPerfilParaPrompt();
      const resposta = await apiClient.post(
        `/api/generate_resume`,
        {
          perfil: perfilTexto,
          descricao_vaga: descricaoVaga,
          customer_id: user.cliente_id
        }
      );

      if(resposta.data.uuid) {
        navigate(`/curriculos/${resposta.data.uuid}`);
      } else {
        setMensagem(resposta.data.error || 'Erro ao gerar currículo.');
      }
    } catch (error) {
      setMensagem(
        error.response?.data?.error || 'Erro ao gerar currículo.'
      );
    } finally {
      setGerando(false);
    }
  };

  // Função para download do PDF
  const handleDownloadPdf = async () => {
    try {
      const response = await apiClient.get(`/api/download_resume/${uuid}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'curriculo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erro ao baixar o PDF!');
    }
  };

  function renderListaSimples(arr) {
    return (
      <ul>
        {arr && arr.map((item, idx) => <li key={idx}>{item}</li>)}
      </ul>
    );
  }

  function renderExperiencias(lista) {
    return (
      <div>
        {lista && lista.map((exp, idx) => (
          <div key={idx} style={{marginBottom:18}}>
            <b>{exp.titulo}</b> <span style={{color:'#888'}}>({exp.data})</span>
            {exp.descricao && exp.descricao.length > 0 && (
              <ul style={{marginTop:6}}>
                {exp.descricao.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  }

  function renderIdiomas(lista) {
    return (
      <ul>
        {lista && lista.map((idioma, idx) => (
          <li key={idx}>
            <b>{idioma.idioma}</b> - {idioma.nivel}
          </li>
        ))}
      </ul>
    );
  }

  function renderContatos(email, telefone, sites) {
  // email e telefone são string, sites é array [{name, link}]
  const contatos = [];
  if (email) contatos.push(<span key="email">{email}</span>);
  if (telefone) contatos.push(<span key="tel">{telefone}</span>);
  if (sites && sites.length > 0) {
    sites.forEach((site, idx) => {
      if (site && site.name && site.link) {
        contatos.push(
          <a
            key={`site${idx}`}
            href={site.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#3572a5', textDecoration: 'none', margin: '0 2px' }}
          >
            {site.name}
          </a>
        );
      }
    });
  }
  // Intercalando ' | '
  return contatos.reduce((prev, cur, idx) => {
    if (idx === 0) return [cur];
    return [...prev, <span key={`sep${idx}`} style={{margin:'0 8px', color:'#888'}}>|</span>, cur];
  }, []);
}

function renderCurriculoBonito(rawCurriculo) {
  if (!rawCurriculo) return null;

  let json;
  try {
    json = typeof rawCurriculo === 'string' ? JSON.parse(rawCurriculo) : rawCurriculo;
  } catch {
    // Não é JSON, fallback para mostrar como texto (exemplo antigo)
    return <pre style={{ whiteSpace:'pre-wrap'}}>{rawCurriculo}</pre>;
  }

  return (
    <div style={{fontFamily:'Inter,Arial,sans-serif', fontSize:16, lineHeight:1.7}}>
      {/* Nome do candidato como título */}
      <h1 style={{
        marginBottom: 4, fontSize: 32, color: "#14365c", fontWeight: 800,
        letterSpacing: 0.5
      }}>
        {json.nome}
      </h1>
      {/* Linha de contatos como subtítulo */}
      <div style={{marginBottom: 22, fontSize: 16, color: "#222"}}>
        {renderContatos(json.email, json.telefone, json.sites)}
      </div>

      <section style={{ marginBottom:22 }}>
        <h2 style={{marginBottom:6, color:'#1e4d80'}}>Introdução</h2>
        <div>{json.introducao}</div>
      </section>
      <section style={{ marginBottom:22 }}>
        <h2 style={{marginBottom:6, color:'#1e4d80'}}>Experiência Profissional</h2>
        {renderExperiencias(json.experiencia_profissional)}
      </section>
      <section style={{ marginBottom:22 }}>
        <h2 style={{marginBottom:6, color:'#1e4d80'}}>Experiência Acadêmica</h2>
        {renderExperiencias(json.experiencia_academica)}
      </section>
      <section style={{ marginBottom:22 }}>
        <h2 style={{marginBottom:6, color:'#1e4d80'}}>Habilidades</h2>
        {renderListaSimples(json.habilidades)}
      </section>
      <section style={{ marginBottom:22 }}>
        <h2 style={{marginBottom:6, color:'#1e4d80'}}>Idiomas</h2>
        {renderIdiomas(json.idiomas)}
      </section>
      {json.outras_experiencias && json.outras_experiencias.length > 0 && (
        <section style={{ marginBottom:22 }}>
          <h2 style={{marginBottom:6, color:'#1e4d80'}}>Outras Experiências</h2>
          {renderExperiencias(json.outras_experiencias)}
        </section>
      )}
    </div>
  );
}

  return (
    <div>
      <h2>Bem-vindo, {user.nome}!</h2>
      <button onClick={() => navigate('/perfil')}>Perfil</button>
      <button onClick={onLogout}>Logout</button>

      <div style={{ marginTop: 30, maxWidth: 600, background: '#f3f3f3', padding: 16, borderRadius: 12 }}>
        <h3>Gerar Currículo Adaptado à Vaga</h3>
        <label>Descrição da vaga:</label>
        <textarea
          value={descricaoVaga}
          onChange={e => setDescricaoVaga(e.target.value)}
          style={{ width: "100%", minHeight: 80 }}
          maxLength={4000}
          placeholder="Cole a descrição da vaga aqui..."
        />
        <div style={{ fontSize: "12px", color: "#666", textAlign: "right" }}>
          {descricaoVaga.length}/4000 caracteres
        </div>
        <br />
        <button onClick={handleGerarCurriculo} disabled={!descricaoVaga || gerando}>
          {gerando ? 'Gerando...' : 'Gerar Currículo'}
        </button>
        {mensagem && <div style={{ color: 'red', marginTop: 12 }}>{mensagem}</div>}

        {curriculo && (
          <>
            <div style={{ margin: '24px 0 8px 0', textAlign: 'right' }}>
              <button onClick={handleDownloadPdf}>Baixar PDF</button>
            </div>
            <div style={{ marginTop: 8, background: "#fff", padding: 24, borderRadius: 12 }}>
              <h4 style={{color:'#275083'}}>Currículo Gerado:</h4>
              {renderCurriculoBonito(curriculo)}
            </div>
          </>
        )}
      </div>
      <button style={{ marginTop: 24 }} onClick={() => navigate('/home')}>Voltar para Home</button>
    </div>
  );
}

export default CurriculoGerado;